import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Download, RotateCcw, Search } from "lucide-react";
import {
  exportActivityLogs,
  getActivityActors,
  getActivityLogs,
} from "../services/activityLogService";

import CustomSelect from "../components/ui/CustomSelect";
const AREA_OPTIONS = [
  { value: "all", label: "All" },
  { value: "inventory", label: "Inventory" },
  { value: "invoices", label: "Invoices" },
  { value: "import", label: "Import" },
  { value: "master_address", label: "Master Address" },
  { value: "sites", label: "Sites" },
  { value: "users", label: "Users" },
];

const PERIOD_OPTIONS = [
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
];

const DEFAULT_FILTERS = {
  search: "",
  area: "all",
  userId: "all",
  period: "30d",
};

const formatWhen = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const periodLabel = (period) =>
  PERIOD_OPTIONS.find((option) => option.value === period)?.label || "Last 30 days";

const SELECT_CLASS =
  "h-[38px] w-full rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-2.5 text-[13px] text-[var(--ink,#1c1c1a)] focus:outline-none focus:ring-2 focus:ring-[var(--muted,#6b6a66)]/20 focus:border-[var(--muted,#6b6a66)]";

const ActivityLogPage = () => {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);

  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
    total: 0,
    totalPages: 1,
  });
  const [actors, setActors] = useState([]);

  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const isFirstLoad = useRef(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((previous) =>
        previous.search === searchInput ? previous : { ...previous, search: searchInput }
      );
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    let cancelled = false;
    getActivityActors()
      .then((response) => {
        if (cancelled) return;
        setActors(response?.data?.actors || []);
      })
      .catch(() => {
        if (!cancelled) setActors([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const queryFilters = useMemo(
    () => ({
      search: filters.search,
      area: filters.area,
      userId: filters.userId,
      period: filters.period,
    }),
    [filters]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getActivityLogs({
        ...queryFilters,
        page,
        pageSize,
      });
      setLogs(response?.data?.logs || []);
      if (response?.data?.pagination) {
        setPagination(response.data.pagination);
      }
    } catch (loadError) {
      setError(loadError?.message || "Failed to load activity logs");
      setLogs([]);
    } finally {
      setLoading(false);
      isFirstLoad.current = false;
    }
  }, [queryFilters, page, pageSize]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const resetFilters = () => {
    setSearchInput("");
    setFilters(DEFAULT_FILTERS);
    setPage(1);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportActivityLogs(queryFilters);
    } catch (exportError) {
      setError(exportError?.message || "Failed to export activity logs");
    } finally {
      setExporting(false);
    }
  };

  const showingFrom = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.pageSize + 1;
  const showingTo = Math.min(pagination.page * pagination.pageSize, pagination.total);

  return (
    <div className="min-h-screen pt-[80px] bg-[var(--ground,#f5f4f1)]">
      <div className="w-full px-5 py-2 pb-14">
        <div className="mb-5 flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4">
          <div>
            <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-[var(--accent,#a51c1c)]">
              Client Administration
            </p>
            <h1 className="mt-1 text-[26px] font-semibold leading-tight text-[var(--ink,#1c1c1a)]">
              Activity log
            </h1>
            <p className="mt-1 text-[13px] text-[var(--muted,#6b6a66)] max-w-xl">
              Who changed what, across inventory, invoices, imports, addresses, sites and users
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-2.5 shrink-0">
            <div className="inline-flex items-stretch overflow-hidden rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white shadow-sm">
              <div className="px-3 py-2">
                <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                  Showing
                </label>
                <div className="mt-0.5 h-7 inline-flex items-center text-[13px] font-medium text-[var(--ink,#1c1c1a)]">
                  {periodLabel(filters.period)}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting || loading}
              className="inline-flex h-[52px] items-center gap-2 rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white px-3.5 text-[13px] font-medium text-[var(--ink,#1c1c1a)] shadow-sm hover:bg-[var(--sunken,#eeece7)] disabled:opacity-50"
            >
              <Download className="w-4 h-4" aria-hidden="true" />
              {exporting ? "Exporting…" : "Export"}
            </button>
          </div>
        </div>

        <div className="mb-4 rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white px-4 py-3">
          <div className="flex flex-col lg:flex-row lg:items-end gap-3 lg:gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] mb-1">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--faint,#9a9a95)] pointer-events-none" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Circuit, account, address or user"
                  className="h-[38px] w-full pl-9 pr-3 rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white text-[13px] text-[var(--ink,#1c1c1a)] placeholder:text-[var(--faint,#9a9a95)] focus:outline-none focus:ring-2 focus:ring-[var(--accent,#a51c1c)]/25 focus:border-[var(--accent,#a51c1c)]"
                />
              </div>
            </div>

            <div className="w-full sm:w-40">
              <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] mb-1">
                Area
              </label>
              <CustomSelect
                value={filters.area}
                onChange={(e) => setFilters((prev) => ({ ...prev, area: e.target.value }))}
                className={SELECT_CLASS}
              >
                {AREA_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </CustomSelect>
            </div>

            <div className="w-full sm:w-44">
              <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] mb-1">
                User
              </label>
              <CustomSelect
                value={filters.userId}
                onChange={(e) => setFilters((prev) => ({ ...prev, userId: e.target.value }))}
                className={SELECT_CLASS}
              >
                <option value="all">All</option>
                {actors.map((actor) => (
                  <option key={actor.id} value={actor.id}>
                    {actor.name}
                  </option>
                ))}
              </CustomSelect>
            </div>

            <div className="w-full sm:w-44">
              <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] mb-1">
                Period
              </label>
              <CustomSelect
                value={filters.period}
                onChange={(e) => setFilters((prev) => ({ ...prev, period: e.target.value }))}
                className={SELECT_CLASS}
              >
                {PERIOD_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </CustomSelect>
            </div>

            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex h-[38px] items-center justify-center gap-1.5 rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-3 text-[13px] font-medium text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)]"
            >
              <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" />
              Reset
            </button>

            <div className="lg:ml-auto text-[13px] text-[var(--muted,#6b6a66)] whitespace-nowrap pb-0.5">
              Showing {showingFrom}-{showingTo} of {pagination.total} entries
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-[10px] border border-[var(--accent,#a51c1c)]/20 bg-[var(--accent-soft,#f3e8e8)] px-4 py-3 text-[13px] text-[var(--accent,#a51c1c)]">
            {error}
          </div>
        )}

        <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-[13px]">
              <thead>
                <tr className="border-b border-[var(--line,#e4e2dc)]">
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                    When
                  </th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                    User
                  </th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                    Area
                  </th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                    Action
                  </th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                    Detail
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading &&
                  Array.from({ length: 8 }).map((_, index) => (
                    <tr key={`skeleton-${index}`} className="border-t border-[var(--line,#e4e2dc)]">
                      {Array.from({ length: 5 }).map((__, cellIndex) => (
                        <td key={cellIndex} className="px-5 py-3.5">
                          <div className="h-3 bg-[var(--sunken,#eeece7)] rounded animate-pulse" />
                        </td>
                      ))}
                    </tr>
                  ))}

                {!loading && logs.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-5 py-12 text-center text-[var(--muted,#6b6a66)]"
                    >
                      No activity in this period.
                    </td>
                  </tr>
                )}

                {!loading &&
                  logs.map((log) => (
                    <tr
                      key={log.id}
                      className="border-t border-[var(--line,#e4e2dc)] hover:bg-[var(--sunken,#eeece7)]"
                    >
                      <td className="px-5 py-3.5 text-[var(--muted,#6b6a66)] whitespace-nowrap align-top">
                        {formatWhen(log.createdAt)}
                      </td>
                      <td className="px-5 py-3.5 text-[var(--ink,#1c1c1a)] whitespace-nowrap align-top">
                        {log.actorName || log.actorEmail || "—"}
                      </td>
                      <td className="px-5 py-3.5 align-top">
                        <span className="inline-flex items-center rounded-full bg-[var(--sunken,#eeece7)] px-2.5 py-0.5 text-[12px] font-medium text-[var(--ink,#1c1c1a)]">
                          {log.areaLabel || log.area}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 font-semibold text-[var(--ink,#1c1c1a)] whitespace-nowrap align-top">
                        {log.actionLabel}
                        {log.status === "failed" && (
                          <span className="ml-2 text-[12px] font-medium text-[var(--accent,#a51c1c)]">
                            Failed
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-[var(--muted,#6b6a66)] align-top max-w-xl">
                        {log.detail || "—"}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-[var(--line,#e4e2dc)] px-5 py-3">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded-md border border-[var(--line-strong,#d5d3cc)] px-3 py-1.5 text-[13px] text-[var(--ink,#1c1c1a)] disabled:opacity-40"
              >
                Previous
              </button>
              <span className="text-[13px] text-[var(--muted,#6b6a66)]">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                type="button"
                disabled={page >= pagination.totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-md border border-[var(--line-strong,#d5d3cc)] px-3 py-1.5 text-[13px] text-[var(--ink,#1c1c1a)] disabled:opacity-40"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityLogPage;
